/**
 * The words Statistik uses for an animal and a report, in the reader's language.
 *
 * Plan rows are stored with Swedish names, and production plans, SMS codes and
 * reports are keyed off them, so the stored value never changes: only the display
 * is translated. A row name a district typed itself ("Hjort t.o.m 12 taggar") has no
 * translation and is shown as stored.
 *
 * The preset names mirror clubhouse's `PRESET_ROW_LABEL_SLUGS`
 * (domains/clubhouse/viltrapport-web/src/features/seasons/speciesTypePresets.ts)
 * and the calf-sex names its `calfSex.moose` / `calfSex.redDeer` keys. Domains may
 * not import each other, so these are copies: keep them in step.
 */

export const ANIMALS_NAMESPACE = 'statisticsAnimals'

export const MOOSE_SPECIES_ID = '1'
export const RED_DEER_SPECIES_ID = '30'

export type CalfSex = 'male' | 'female'

const PRESET_ROW_SLUGS: Readonly<Record<string, string>> = {
  kalv: 'calf',
  'ko, kviga': 'cowHeifer',
  tjur: 'bull',
  hind: 'hind',
  hjort: 'stag',
  kid: 'kid',
  get: 'doe',
  bock: 'buck',
  kulting: 'piglet',
  sugga: 'sow',
  galt: 'boar',
}

const REPORT_TYPES: ReadonlySet<string> = new Set(['kill', 'observation', 'algobs'])
/**
 * Statuses that need someone's action, the only ones Statistik labels, in legacy's
 * wording. Legacy showed no status on a finished report, and the new app stores every
 * report as `approved` the moment it is filed with nothing ever reviewing it, so
 * "Godkänd" would claim an approval that never happened. A rejected report is labelled,
 * because it counts nowhere and must not look like one that does.
 * Mirrors clubhouse's reportState, since domains may not import each other.
 */
export type LabelledReportState = 'completion' | 'completionOverdue' | 'review' | 'rejected'

/**
 * A report's state for labelling, or '' for one that is not labelled. What a waiting
 * report waits for comes from the API (`waitingFor`), which decides it from the stored
 * status and the legacy flag, because both legacy waiting states were migrated as
 * `submitted`. The API reads every stored status into one of the four it knows, so there
 * is no other value to show.
 */
export function reportState(
  status: string,
  waitingFor?: string | null,
  completionOverdue?: boolean,
): LabelledReportState | '' {
  if (status === 'rejected') return 'rejected'
  // Overdue is legacy's "Komplettering försenad": the API says so only for a report that
  // waits for completion, once the area's completion days have run out.
  if (waitingFor === 'completion') return completionOverdue ? 'completionOverdue' : 'completion'
  if (waitingFor === 'review') return 'review'
  return ''
}

export interface AnimalLabels {
  /** A plan row's or category's stored name, translated when it is a preset. */
  row(name: string): string
  /** "Tjurkalv" etc. Null for a species the API never stores a calf sex for. */
  calfSex(speciesId: string, sex: CalfSex): string | null
  /** A report's `type`; an unknown value is returned as it is. */
  reportType(wire: string): string
  /**
   * A note for a report's `status`: legacy's wording for one that waits, '' for a
   * finished one. A submitted report is already
   * in Statistik's totals while it waits, as in legacy: the note says only that nobody
   * has looked at it yet.
   */
  reportStatus(wire: string, waitingFor?: string | null, completionOverdue?: boolean): string
  /** The note for a state `reportState` already worked out. */
  reportStateText(state: LabelledReportState): string
}

const presetSlug = (name: string): string | undefined => PRESET_ROW_SLUGS[name.trim().toLocaleLowerCase('sv')]

/**
 * Whether a row is named only by a preset category ("Kalv"), which on its own does not
 * say which species it belongs to; a district's own wording ("Kron hankalv") does.
 */
export function isPresetRowName(name: string): boolean {
  return presetSlug(name) !== undefined
}

export function animalLabels(t: (key: string) => string): AnimalLabels {
  const reportStateText = (state: LabelledReportState) => t(`reportStatus.${state}`)
  return {
    row(name) {
      const slug = presetSlug(name)
      return slug === undefined ? name : t(`rowName.${slug}`)
    },
    calfSex(speciesId, sex) {
      if (speciesId === MOOSE_SPECIES_ID) return t(`calfSex.moose.${sex}`)
      if (speciesId === RED_DEER_SPECIES_ID) return t(`calfSex.redDeer.${sex}`)
      return null
    },
    reportType: (wire) => (REPORT_TYPES.has(wire) ? t(`reportType.${wire}`) : wire),
    reportStatus: (wire, waitingFor, completionOverdue) => {
      const state = reportState(wire, waitingFor, completionOverdue)
      return state === '' ? '' : reportStateText(state)
    },
    reportStateText,
  }
}
